import {
  flushOutbox,
  type FieldAppointmentApi,
  type InspectionStepApi,
  type PassageNoticeApi,
  type SyncApi,
} from './outbox'
import { shipLogs as shipPendingLogs, type LogShipApi } from './logger'
import { readDevicePreferences } from './preferences'
import { pullMediaPolicy } from './media-policy'
import { pullInquiries, pullInternalMeetings, pullVocabulary, type PullApi } from './pull'
import type { SharedDatabase } from './shared-db'
import type { TeamDataStore } from './team-data'

/**
 * Quand la synchro repart, et ce qu'un échec emporte avec lui.
 *
 * Le démarrage et l'événement `online` ne suffisent pas : un réseau qui ne tombe jamais
 * n'émet jamais `online`, et une fiche transmise à 9 h resterait `pending` jusqu'au
 * redémarrage de l'app — quatre barres de réseau à l'écran. D'où trois déclencheurs de plus :
 * un minuteur, le retour au premier plan, et une demande explicite (`requestSync`) que les
 * écrans appelleront après une transmission.
 *
 * ⚠️ Les étapes sont **isolées**. Enchaînées par `await` dans une même fonction, un
 * `QuotaExceededError` d'une seule entrée de la file faisait sauter les deux descentes : le
 * lendemain, l'agent partait avec la tournée de la veille et un référentiel périmé, pour une
 * `unhandledrejection` en console et rien à l'écran. L'ordre « la saisie part d'abord » reste
 * — c'est la priorité, pas une dépendance.
 */

/**
 * Cadence du minuteur.
 *
 * Une minute borne l'attente visible : l'agent qui transmet voit sa fiche partir avant d'avoir
 * rangé la tablette. Le tour à vide ne coûte qu'une lecture indexée locale — `flushOutbox`
 * rend la main sans réseau quand la file est vide, et `retryRefresh` ne fait rien quand la
 * session est déjà en ligne. Descendre plus bas n'achèterait rien de perceptible.
 */
export const SYNC_INTERVAL_MS = 60_000

/**
 * Fraîcheur de la tournée et du référentiel.
 *
 * Ce qui descend est **coûteux** (deux requêtes, sur la 4G d'un chantier) et bouge peu : le
 * redemander toutes les minutes serait payé pour rien. Un tour de minuteur ne redescend donc
 * qu'au-delà de ce délai ; un retour du réseau ou une demande explicite passe outre — ce sont
 * précisément les moments où la tournée a pu changer.
 */
export const PULL_MAX_AGE_MS = 10 * 60_000

/**
 * Ce qu'une passe dit à ses étapes. `forced` — reconnexion ou demande d'un écran : le moment
 * où rejouer a le plus de chances d'aboutir, donc où la file passe outre ses reprises
 * programmées (`nextAttemptAt`). Une tournée périmée redescend sans rendre la passe forcée.
 */
export interface SyncStepContext {
  forced: boolean
  /**
   * **L'agent a demandé cette passe**, par un bouton ou en transmettant — par opposition au
   * minuteur, au retour du réseau et au retour au premier plan.
   *
   * ⚠️ Ce n'est **pas** `forced`, et les confondre était le piège : le retour du réseau est
   * `forced` (c'est le moment où rejouer a le plus de chances d'aboutir) tout en étant
   * exactement ce que le réglage « Synchro automatique » doit pouvoir retenir. Un seul drapeau
   * pour les deux questions aurait laissé partir la file à chaque reconnexion, réglage éteint.
   */
  requested: boolean
}

/** Une étape de synchro. Elle aboutit, ou elle échoue **sans emporter les autres**. */
export type SyncStep = (context: SyncStepContext) => Promise<unknown>

/**
 * Les six gestes d'une passe, injectés : c'est ce qui rend l'ordre et l'isolation testables
 * sans réseau ni base.
 */
export interface SyncSteps {
  /** Rend un jeton d'accès frais à une session dégradée ; sans effet sinon. */
  refresh: SyncStep
  flush: SyncStep
  pullInquiries: SyncStep
  /** Les points internes de l'équipe (#319) — après la tournée, qui reste ce qui compte d'abord. */
  pullInternalMeetings: SyncStep
  pullVocabulary: SyncStep
  /**
   * Le journal de diagnostic (D15) — **en dernier, toujours**. C'est de la télémétrie : elle
   * ne dispute jamais la bande passante à une saisie qui monte ni à une tournée qui descend.
   */
  shipLogs: SyncStep
}

export type SyncStepName = keyof SyncSteps

/** Ce qu'une étape a donné. `skipped` : le cache était encore frais, rien n'a été demandé. */
export type SyncStepOutcome = 'done' | 'failed' | 'skipped'

export type SyncPassReport = Record<SyncStepName, SyncStepOutcome>

/** Ce qu'un échec d'étape devient : une trace, jamais une exception qui remonte. */
export type SyncFailureReporter = (step: string, error: unknown) => void

export function reportSyncFailure(step: string, error: unknown): void {
  console.error(`[sync] Étape « ${step} » en échec ; les autres continuent.`, error)
}

export interface SyncPassOptions {
  /** `false` sur un tour de minuteur dont la tournée est encore fraîche. */
  pull?: boolean
  /**
   * Reconnexion ou demande explicite d'un écran — jamais un simple tour de minuteur, même
   * quand sa tournée est à redescendre : « périmé depuis dix minutes » n'est pas « quelqu'un
   * l'a demandé », et la file ne doit sauter ses reprises programmées que pour le second.
   */
  forced?: boolean
  /** Cf. {@link SyncStepContext.requested} : une demande de l'agent, pas un déclencheur de fond. */
  requested?: boolean
  onFailure?: SyncFailureReporter
}

/** Exécute une étape jusqu'au bout : son échec devient un compte rendu, pas un rejet. */
async function settle(
  name: SyncStepName,
  step: SyncStep,
  context: SyncStepContext,
  onFailure: SyncFailureReporter,
): Promise<SyncStepOutcome> {
  try {
    await step(context)
    return 'done'
  } catch (error) {
    onFailure(name, error)
    return 'failed'
  }
}

/**
 * Une passe complète. **Ne rejette jamais** : elle rend ce que chaque étape a donné.
 *
 * Les étapes se suivent au lieu de partir ensemble — sur un lien de chantier, trois requêtes
 * concurrentes se gênent, et la saisie doit avoir la bande passante d'abord.
 */
export async function runSyncPass(
  steps: SyncSteps,
  options: SyncPassOptions = {},
): Promise<SyncPassReport> {
  const onFailure = options.onFailure ?? reportSyncFailure
  const context: SyncStepContext = {
    forced: options.forced === true,
    requested: options.requested === true,
  }

  const refresh = await settle('refresh', steps.refresh, context, onFailure)
  // Ce qui est saisi part d'abord : une tournée retrouvée est un confort, une fiche perdue ne
  // se reconstitue pas.
  const flush = await settle('flush', steps.flush, context, onFailure)

  if (options.pull === false) {
    return {
      refresh,
      flush,
      pullInquiries: 'skipped',
      pullInternalMeetings: 'skipped',
      pullVocabulary: 'skipped',
      // Le journal part même sur un tour de minuteur qui ne redescend rien : c'est bon marché,
      // et une tablette qui ne synchronise plus est justement celle qu'on veut pouvoir lire.
      shipLogs: await settle('shipLogs', steps.shipLogs, context, onFailure),
    }
  }

  return {
    refresh,
    flush,
    pullInquiries: await settle('pullInquiries', steps.pullInquiries, context, onFailure),
    pullInternalMeetings: await settle(
      'pullInternalMeetings',
      steps.pullInternalMeetings,
      context,
      onFailure,
    ),
    pullVocabulary: await settle('pullVocabulary', steps.pullVocabulary, context, onFailure),
    shipLogs: await settle('shipLogs', steps.shipLogs, context, onFailure),
  }
}

export interface SyncScheduler {
  /**
   * Demande une passe maintenant — après une transmission, ou depuis un bouton « synchroniser ».
   * Rend une promesse qui **ne rejette jamais**, résolue quand la passe (et son éventuel
   * rattrapage) est terminée.
   */
  requestSync(): Promise<void>
  stop(): void
}

export interface SyncSchedulerOptions {
  intervalMs?: number
  pullMaxAgeMs?: number
  /** Injectée pour que les tests puissent avancer l'horloge sans attendre dix minutes. */
  now?: () => number
  onFailure?: SyncFailureReporter
}

/**
 * Branche les déclencheurs et rend de quoi les débrancher.
 *
 * Ne lance **pas** de passe de lui-même : la première attend que la session locale soit lue,
 * et c'est le point d'entrée qui le sait (`main.tsx`).
 */
export function startSyncScheduler(
  steps: SyncSteps,
  options: SyncSchedulerOptions = {},
): SyncScheduler {
  const intervalMs = options.intervalMs ?? SYNC_INTERVAL_MS
  const pullMaxAgeMs = options.pullMaxAgeMs ?? PULL_MAX_AGE_MS
  const now = options.now ?? (() => Date.now())

  let inFlight = Promise.resolve()
  let running = false
  let queued = false
  let queuedPull = false
  let queuedRequested = false
  let lastPullAt: number | null = null

  function pullIsDue(forced: boolean): boolean {
    if (forced) return true
    return lastPullAt === null || now() - lastPullAt >= pullMaxAgeMs
  }

  async function runUntilSettled(firstForcedPull: boolean, firstRequested: boolean): Promise<void> {
    running = true
    let forcedPull = firstForcedPull
    let requested = firstRequested

    try {
      for (;;) {
        queued = false
        queuedPull = false
        queuedRequested = false

        const pull = pullIsDue(forcedPull)
        // Horodaté avant l'appel : une descente qui échoue faute de réseau ne doit pas être
        // reprise à chaque tour de minuteur — le retour du réseau, lui, passe outre.
        if (pull) lastPullAt = now()

        // `forced` vient de la demande, jamais de `pull` : un tour de minuteur dont la
        // tournée est périmée redescend (`pull: true`) sans faire sauter à la file ses
        // reprises programmées.
        await runSyncPass(steps, {
          pull,
          forced: forcedPull,
          requested,
          onFailure: options.onFailure,
        })

        if (!queued) return
        forcedPull = queuedPull
        // Une demande reçue en vol ne se perd pas : le rattrapage la porte, sinon une
        // transmission faite pendant une passe de fond attendrait, réglage éteint, indéfiniment.
        requested = queuedRequested
      }
    } finally {
      running = false
    }
  }

  /**
   * Une seule passe à la fois. Doubler les passes ne ferait que dupliquer les requêtes — c'est
   * exactement ce qui arrive à la reconnexion, où plusieurs déclencheurs tombent ensemble. Une
   * demande reçue en vol n'est pas perdue pour autant : elle programme un rattrapage derrière,
   * sans quoi une fiche mise en file pendant la passe attendrait le tour suivant.
   */
  function requestPass(forcedPull: boolean, requested: boolean): Promise<void> {
    if (running) {
      queued = true
      queuedPull = queuedPull || forcedPull
      queuedRequested = queuedRequested || requested
      return inFlight
    }

    inFlight = runUntilSettled(forcedPull, requested)

    return inFlight
  }

  const onOnline = (): void => void requestPass(true, false)
  const onVisibilityChange = (): void => {
    // Retour au premier plan : la tablette a pu passer des heures écran éteint, et rien ne
    // garantit qu'un `online` soit passé pendant ce temps.
    if (document.visibilityState === 'visible') void requestPass(false, false)
  }
  const onTick = (): void => void requestPass(false, false)

  const timer = setInterval(onTick, intervalMs)
  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisibilityChange)

  return {
    requestSync: () => requestPass(true, true),
    stop() {
      clearInterval(timer)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    },
  }
}

export interface SyncDependencies {
  /**
   * La base de l'équipe connectée, relue à chaque étape : la tablette passe de main en main, et
   * c'est le porteur qui sait laquelle est ouverte (D22 pt.4). `null` tant qu'aucune session
   * n'a nommé d'équipe — une passe n'a alors ni file à vider ni tournée où ranger.
   */
  data: TeamDataStore
  /** Le référentiel, le journal et les réglages : ce qui n'appartient à aucune équipe. */
  shared: SharedDatabase
  api: SyncApi & PassageNoticeApi & FieldAppointmentApi & InspectionStepApi & PullApi & LogShipApi
  /** Relu à chaque étape, pas capturé : un jeton d'accès vaut quinze minutes (M23). */
  getAccessToken: () => string | null
  refreshSession: () => Promise<unknown>
  /**
   * Un envoi ou une descente a pris un `401` avec ce jeton : au détenteur de la session de
   * redescendre en dégradé, pour que le `refresh` de la passe suivante reparte vraiment.
   */
  onUnauthorized?: (accessToken: string) => void
  /**
   * Une descente a écrit des lignes : à l'appelant d'invalider la clé Query concernée (D21).
   * Ces tables n'ont pas d'observateur `liveQuery` — sans ce rappel, une tournée descendue en
   * arrière-plan ne s'affiche qu'à la navigation suivante.
   */
  onPulled?: (table: PulledTable) => void
}

/** Les tables qu'une descente réécrit — celles dont l'appelant invalide la lecture. */
export type PulledTable = 'inquiries' | 'internalMeetings' | 'vocabulary'

/** Relie les étapes à la file et aux descentes réelles. */
export function createSyncSteps(deps: SyncDependencies): SyncSteps {
  const pulled = <Result extends { outcome: string }>(
    table: PulledTable,
    result: Result,
  ): Result => {
    // `partial` a pu écrire avant d'échouer : ce qui est rangé doit se voir quand même.
    if (result.outcome !== 'unavailable') deps.onPulled?.(table)
    return result
  }

  return {
    refresh: () => deps.refreshSession(),
    flush: async (context) => {
      /**
       * ⚠️ **« Synchro automatique » retient le fond, jamais un geste de l'agent.** Le réglage
       * dit « envoyer les contrôles dès qu'une connexion est disponible » : c'est le minuteur,
       * le retour du réseau et le retour au premier plan qu'il éteint. Transmettre, appuyer sur
       * « Synchroniser » ou réessayer depuis le panneau de connexion partent toujours — une
       * saisie qu'aucun geste ne peut faire sortir de la tablette serait exactement la perte
       * contre laquelle D4 et D22 protègent.
       *
       * ⚠️ La file n'est ni vidée ni marquée : les entrées restent `pending` et repartiront
       * telles quelles. Le journal de synchro continue donc de dire ce qui attend.
       */
      if (!context.requested && !(await readDevicePreferences(deps.shared)).autoSync) return

      /**
       * ⚠️ **Le jeton et la base viennent de la même session, envoi par envoi.** Une tablette
       * qui change de main pendant qu'une file de vidéos monte déposerait sinon les envois de
       * l'équipe sortante sous l'identité de l'entrante — le serveur filtrant par l'équipe du
       * jeton, un `403` que `isTransient` classe définitif, et une fiche du matin qui réclame un
       * ré-armement à la main pour revenir.
       *
       * La base, elle, **ne peut pas** changer en cours de passe : la passe lit une file, et
       * changer de file en route est ce que la carte par base de `flushOutbox` interdit. C'est
       * donc le jeton qui est vérifié contre la base de la passe.
       */
      const db = deps.data.current()
      if (db === null) return

      const tokenOfThisPass = (): string | null => {
        // Le jeton reste **relu envoi par envoi** : il vaut quinze minutes (M23), une file de
        // vidéos dure plus longtemps, et lu une seule fois en tête de passe il expirerait au
        // milieu et enverrait tout le reste en échec.
        if (deps.data.current() === db) return deps.getAccessToken()

        // La tablette a changé d'équipe : `null`, exactement comme une session dégradée. La
        // passe s'arrête là, rien n'est perdu, les entrées restantes restent rejouables — et la
        // file de l'équipe entrante est une autre file, dans une autre base, que sa propre passe
        // lira.
        return null
      }

      return flushOutbox(db, deps.api, tokenOfThisPass, {
        forced: context.forced,
        onUnauthorized: deps.onUnauthorized,
      })
    },
    pullInquiries: () =>
      pullInquiries(
        deps.data.current(),
        deps.shared,
        deps.api,
        deps.getAccessToken(),
        deps.onUnauthorized,
      ).then((result) => pulled('inquiries', result)),
    pullInternalMeetings: () =>
      pullInternalMeetings(
        deps.data.current(),
        deps.api,
        deps.getAccessToken(),
        deps.onUnauthorized,
      ).then((result) => pulled('internalMeetings', result)),
    pullVocabulary: async () => {
      // La limite vidéo du serveur (#418) voyage avec le référentiel : comme lui, elle n'est à
      // aucune équipe, et ne coûte qu'une petite lecture.
      await pullMediaPolicy(deps.api, deps.getAccessToken())

      // Le référentiel d'anomalies n'appartient à aucune équipe : le jeton suffit.
      return pullVocabulary(deps.shared, deps.api, deps.getAccessToken(), deps.onUnauthorized).then(
        (result) => pulled('vocabulary', result),
      )
    },
    shipLogs: () => shipPendingLogs(deps.shared, deps.api, deps.getAccessToken),
  }
}
